import { useEffect, useState } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addEmployee,
  getAllShifts,
  getEmployeeById,
  updateEmployee,
} from "../../services/services";
import Loading from "../../components/loading";

const AddEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    department: "",
    role: "",
    shift: "",
    joinDate: new Date().toISOString().split("T")[0],
  });

  const fetchEmployeeById = async () => {
    setLoading(true);
    try {
      const res = await getEmployeeById(id);
      if (res.data.status) {
        const emp = res.data.response;

        setForm({
          name: emp.name || "",
          mobile: emp.mobile || "",
          email: emp.email || "",
          department: emp.department || "",
          role: emp.role || "",
          shift: emp.shift?._id || "",
          joinDate: emp.joinDate ? emp.joinDate.split("T")[0] : "",
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load employee data",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({ ...form, [name]: value });

    // Remove error for that field
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const res = await getAllShifts();
      if (res.data.status) {
        setShifts(res.data.response || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load shifts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  useEffect(() => {
    if (id) {
      fetchEmployeeById();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Full Name is required";
    }

    if (!form.mobile.trim()) {
      newErrors.mobile = "Mobile is required";
    }

    if (!form.shift) {
      newErrors.shift = "Shift is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      // Focus first error field
      const firstErrorKey = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorKey}"]`);
      if (element) element.focus();

      // Show first error in toast
      toast.error(newErrors[firstErrorKey]);

      return;
    }

    setErrors({}); // clear errors if valid
    setSubmit(true);
    try {
      let res;

      if (id) {
        res = await updateEmployee(id, form);
      } else {
        res = await addEmployee(form);
      }

      if (res.data.status) {
        toast.success(res.data.message);
        navigate("/employee");
      } else {
        toast.error(res.data.message || "Failed to add employee");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Server error. Could not add employee.",
      );
    } finally {
      setSubmit(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div id="main">
          <div className="d-flex align-items-center mb-4">
            <Link to="/employee" className="me-3">
              <i className="bi bi-arrow-left fs-4"></i>
            </Link>
            <h4 className="custom-title mb-0">
              {id ? "Update" : "Add"} Employee
            </h4>
          </div>

          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Full Name
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      value={form.name}
                      onChange={handleChange}
                    />
                    {errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Mobile
                      <span className="text-danger ms-1">*</span>
                    </label>
                    {/* <input
                      type="tel"
                      name="mobile"
                      className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                      value={form.mobile}
                      // onChange={(e) => handleChange}
                      onChange={handleChange}
                      maxLength={10}
                    /> */}

                    <input
                      type="tel"
                      name="mobile"
                      className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                      value={form.mobile}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "mobile",
                            value: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          },
                        })
                      }
                      maxLength={10}
                      inputMode="numeric"
                      pattern="[6-9][0-9]{9}"
                      required
                    />

                    {errors.mobile && (
                      <div className="invalid-feedback">{errors.mobile}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Department</label>
                    <input
                      type="text"
                      name="department"
                      className="form-control"
                      value={form.department}
                      onChange={handleChange}
                      placeholder="e.g. Sales, Accounts, Delivery"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Role</label>
                    <input
                      type="text"
                      name="role"
                      className="form-control"
                      value={form.role}
                      onChange={handleChange}
                      placeholder="e.g. Manager, Supervisor, Operator"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Shift
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.shift ? "is-invalid" : ""}`}
                      name="shift"
                      value={form.shift}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select Shift
                      </option>
                      {shifts.length == 0 ? (
                        <option value="" disabled>
                          No shifts available
                        </option>
                      ) : (
                        <>
                          {shifts.map((s) => (
                            <option key={s._id} value={s._id}>
                              {s.name}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                    {errors.shift && (
                      <div className="invalid-feedback">{errors.shift}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Join Date</label>
                    <input
                      type="date"
                      name="joinDate"
                      className="form-control"
                      value={form.joinDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="mt-4 d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => navigate("/employee")}
                    disabled={submit}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-sm btn-primary"
                    disabled={submit}
                  >
                    {id ? "Update Employee" : "Save Employee"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default headerLayoutWrapper(AddEmployee);
